import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LotusMark } from "./LotusMark";
import { site } from "../../config/site";
import { scrollToSection as scrollTo } from "./lenis";

const LINKS = [
  { label: "Home", id: "hero" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Reviews", id: "testimonials" },
  { label: "Contact", id: "quote" },
];

export const Navbar = () => {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(latest > prev && latest > 200);
    setScrolled(latest > 40);
  });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -100 }}
      animate={{ y: hidden ? -120 : 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="fixed top-0 inset-x-0 z-50"
    >
      <div
        className={`transition-colors duration-300 ${
          scrolled
            ? "bg-[#111111]/85 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-[76px]">
          <button
            data-testid="logo-home-btn"
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-2.5 group"
          >
            <LotusMark size={38} className="transition-transform duration-500 group-hover:rotate-[8deg]" />
            <span className="leading-none text-left">
              <span className="block font-heading font-extrabold tracking-tight text-xl text-white">LOTUS</span>
              <span className="block text-[9px] tracking-[0.3em] text-[#53C653] font-semibold">PROPERTY SERVICES</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="relative text-sm font-medium text-white/80 hover:text-white transition-colors duration-200 group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-[#F56FB4] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-quote-btn"
              onClick={() => scrollTo("quote")}
              className="hidden sm:inline-flex items-center rounded-full bg-[#F56FB4] px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-105 shadow-[0_0_20px_rgba(245,111,180,0.4)]"
            >
              Get a Free Quote
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#111111] border-b border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left py-3 text-lg font-heading font-semibold text-white/90 border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={site.phoneHref || "#quote"}
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#53C653] text-[#53C653] py-3 font-semibold"
              >
                <Phone size={18} /> Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
